import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider, FormControl, Input, InputLabel, MenuItem, Select, SelectChangeEvent, Stack, TextField, Typography } from '@mui/material';
import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { LanguageLevelEnum, LanguageLevelLabels } from '../../api/ChatApi';
import { CourseApi, ICourse } from '../../api/CourseApi';
import { DialogApi, IDialog } from '../../api/DialogApi';
import { DialogPlanApi, IDialogPlan, IGeneratedDialogPlanResponse } from '../../api/DialogPlanApi';
import { DialogPlanLlmPrompt, DialogPlanLlmPromptApi } from '../../api/DialogPlanLlmPromptApi';
import { DialogPromptApi, DialogPromptQuizWord } from '../../api/DialogPromptApi';
import { DialogPromptConversationApi } from '../../api/DialogPromptConversationApi';
import { ILesson, LessonApi } from '../../api/LessonApi';
import { IVocabularyWord } from '../../api/VocabularyWordApi';
import AppBarContext from '../../context/AppBarContext';
import { useCustomInputTextPinyinList } from '../../hook/useCustomInputTextPinyin';
import { useVocabularyWordListPinyin } from '../../hook/useVocabularyWordListPinyin';
import { handleErrorMessage, useErrorDialog } from '../ErrorDialog';
import { arrayBufferToBase64 } from '../../util/base64Utils';
import DialogPlanLlmPromptForm from './DialogPlanLlmPromptForm';
import DialogPlanLlmPromptGenerated from './DialogPlanLlmPromptGenerated';

interface DialogPlanFormProps {
    dialogPlanId: number;
    onSubmit: (updatedDialogPlan: IDialogPlan) => void;
    onDelete: (dialogPlanId: number) => void;
}

function mergeGeneratedVocabularyWordFields(
    currentWord: IVocabularyWord | undefined,
    generatedWord: IVocabularyWord | undefined,
    fieldNames: Array<keyof IVocabularyWord>,
): IVocabularyWord | undefined {
    if (!generatedWord) {
        return currentWord;
    }
    const mergedWord = { ...(currentWord ?? generatedWord) } as IVocabularyWord;
    fieldNames.forEach((fieldName) => {
        if (generatedWord[fieldName] !== undefined) {
            (mergedWord as any)[fieldName] = generatedWord[fieldName];
        }
    });
    return mergedWord;
}

function mergeGeneratedVocabularyWordListFields(
    currentWordList: IVocabularyWord[] | undefined,
    generatedWordList: IVocabularyWord[] | undefined,
    fieldNames: Array<keyof IVocabularyWord>,
): IVocabularyWord[] | undefined {
    if (!generatedWordList) {
        return currentWordList;
    }
    const currentList = currentWordList ?? [];
    const maxLength = Math.max(currentList.length, generatedWordList.length);
    return Array.from({ length: maxLength }, (_, index) =>
        mergeGeneratedVocabularyWordFields(currentList[index], generatedWordList[index], fieldNames),
    ).filter(Boolean) as IVocabularyWord[];
}

function mergeGeneratedDialogPromptField(currentPrompt: any, generatedPrompt: any, fieldName: string) {
    if (fieldName === 'correct_vocabulary_word_info_text') {
        return {
            ...currentPrompt,
            correct_vocabulary_word: mergeGeneratedVocabularyWordFields(
                currentPrompt.correct_vocabulary_word,
                generatedPrompt.correct_vocabulary_word,
                ['word_info_text'],
            ),
        };
    }
    if (fieldName === 'correct_vocabulary_word_image') {
        return {
            ...currentPrompt,
            correct_vocabulary_word: mergeGeneratedVocabularyWordFields(
                currentPrompt.correct_vocabulary_word,
                generatedPrompt.correct_vocabulary_word,
                ['image', 'image_url'],
            ),
        };
    }
    if (fieldName === 'incorrect_vocabulary_word_info_text_list') {
        return {
            ...currentPrompt,
            incorrect_vocabulary_word_list: mergeGeneratedVocabularyWordListFields(
                currentPrompt.incorrect_vocabulary_word_list,
                generatedPrompt.incorrect_vocabulary_word_list,
                ['word_info_text'],
            ),
        };
    }
    if (fieldName === 'incorrect_vocabulary_word_image_list') {
        return {
            ...currentPrompt,
            incorrect_vocabulary_word_list: mergeGeneratedVocabularyWordListFields(
                currentPrompt.incorrect_vocabulary_word_list,
                generatedPrompt.incorrect_vocabulary_word_list,
                ['image', 'image_url'],
            ),
        };
    }
    return {
        ...currentPrompt,
        [fieldName]: generatedPrompt[fieldName],
    };
}

function getDialogPromptVocabularyWord(dialogPrompt: any, index: number): IVocabularyWord | undefined {
    const list = dialogPrompt?.vocabulary_word_list;
    return Array.isArray(list) ? list[index] : undefined;
}

function getEffectiveVocabularyWord(
    vocabularyWordList: IVocabularyWord[],
    dialogPrompt: any,
    index: number,
): string | undefined {
    const word = vocabularyWordList[index]?.word;
    if (word && word.trim().length > 0) {
        return word;
    }
    return getDialogPromptVocabularyWord(dialogPrompt, index)?.word;
}

function getEffectiveVocabularyWordPinyinList(
    vocabularyWordList: IVocabularyWord[],
    dialogPrompt: any,
    getVocabularyWordPinyinListMap: (index: number, key: 'word_pinyin') => string[],
    index: number,
): string[] | undefined {
    const pinyinList = getVocabularyWordPinyinListMap(index, 'word_pinyin');
    if (pinyinList.length > 0) {
        return pinyinList;
    }
    return getDialogPromptVocabularyWord(dialogPrompt, index)?.word_pinyin;
}

const supportedVariableList = ['<VocabularyWord1/>', '<VocabularyWord2/>', '<VocabularyWord3/>', '<VocabularyWord4/>', '<VocabularyWord5/>',
    '<VocabularyWord6/>', '<VocabularyWord7/>', '<VocabularyWord8/>', '<VocabularyWord9/>', '<VocabularyWord10/>',
    '<VocabularyWord1Pinyin/>', '<VocabularyWord2Pinyin/>', '<VocabularyWord3Pinyin/>', '<VocabularyWord4Pinyin/>', '<VocabularyWord5Pinyin/>',
    '<VocabularyWord6Pinyin/>', '<VocabularyWord7Pinyin/>', '<VocabularyWord8Pinyin/>', '<VocabularyWord9Pinyin/>', '<VocabularyWord10Pinyin/>',
    '<VocabularyWord/>', '<StudentName/>', '<TeacherName/>', '<Microphone/>', '<Speaker/>', '<Info/>', '<Feedback/>', '<Answer/>',
    '<IncorrectVocabularyWord/>', '<SkipLLM/>', '<Minutes/>', '<SentenceCount/>'];
const supportedVariableSet = new Set(supportedVariableList.map(variable => variable.toLowerCase()));
const isSupportedVariable = (variable: string): boolean => supportedVariableSet.has(variable.toLowerCase());

const DialogPlanForm: React.FC<DialogPlanFormProps> = ({ dialogPlanId, onSubmit, onDelete }) => {

    const { setErrorMessage } = React.useContext(AppBarContext)
    const { showErrorDialog, dialog } = useErrorDialog();
    const history = useHistory();
    const location = useLocation();

    const [id, setId] = React.useState(-1);
    const [name, setName] = React.useState('');
    const [dialogTitle, setDialogTitle] = React.useState('');
    const [dialogSubtitle, setDialogSubtitle] = React.useState('');
    const [showConfirmation, setShowConfirmation] = React.useState(false);
    // const [hskLevel, setHskLevel] = React.useState(1);
    const [languageLevel, setLanguageLevel] = React.useState(LanguageLevelEnum.Hsk1);

    const [variableList, setVariableList] = React.useState<string[]>([])
    const [customVariableList, setCustomVariableList] = React.useState<string[]>([]);
    const [unsupportedVariableList, setUnsupportedVariableList] = React.useState<string[]>([])
    const [customInputMap, setCustomInputMap] = React.useState<Record<string, string | string[]>>({});
    const [customInputMapValidation, setCustomInputMapValidation] = React.useState<Record<string, string>>({});

    const {
        vocabularyWordList,
        getVocabularyWordPinyinMap,
        getVocabularyWordPinyinListMap,
        getVocabularyWordErrorMap,
        setVocabularyWordList,
        handleVocabularyWordTextChange,
        handleVocabularyWordPinyinChange,
    } = useVocabularyWordListPinyin({ history, location, setErrorMessage })

    const {
        customInputTextMap,
        customInputTextPinyinMap, // warning key variable name, does NOT Pinyin/> suffix
        customInputTextPinyinListMap, // warning key variable name, has Pinyin/> suffix
        customInputTextPinyinErrorMap,
        isCustomInputTextVariable,
        handleCustomInputTextChange,
        handleCustomInputTextPinyinChange,
        setCustomInputTextVariableList,
    } = useCustomInputTextPinyinList({ history, location, setErrorMessage })


    const [generatedDialogTitle, setGeneratedDialogTitle] = React.useState('');
    const [generatedDialogSubtitle, setGeneratedDialogSubtitle] = React.useState<string | undefined>(undefined);
    const [generatedDialogPlanResponse, setGeneratedDialogPlanResponse] = React.useState<IGeneratedDialogPlanResponse | null>(null)
    const [isGenerating, setIsGenerating] = React.useState(false);
    const hasScrolledToGeneratedPrompt = React.useRef(false); // Use ref to track if scrolling has occurred

    const [isRegenerating, setIsRegenerating] = React.useState<{ [key: number]: boolean }>({});
    const [isInserting, setIsInserting] = React.useState(false);

    const [courseList, setCourseList] = React.useState<ICourse[]>([]); // State to store course options
    const [lessonDictionary, setLessonDictionary] = React.useState<{ [key: number]: ILesson[]; }>({});
    const [dialogDictionary, setDialogDictionary] = React.useState<{ [key: number]: IDialog[]; }>([]);
    const [insertLessonAndDialogId, setInsertLessonAndDialogId] = React.useState<string>("");


    const fetchCourseList = React.useCallback(async () => {
        try {
            const response = await CourseApi.getAll();
            setCourseList(response.data);
        } catch (err: any) {
            handleErrorMessage(err, setErrorMessage, history, location);
        }
    }, [setCourseList, setErrorMessage, history, location]);
    const fetchLessonDictionary = React.useCallback(async () => {
        try {
            const response = await LessonApi.getAll();
            const lessonList = response.data

            const lessonDictionary = lessonList.reduce((accumulator, currentLesson) => {
                const courseId = currentLesson.course_id;

                // If course_id exists in the accumulator, push the lesson into the array
                if (accumulator[courseId]) {
                    accumulator[courseId].push(currentLesson);
                } else {
                    // Otherwise, initialize an array with the current lesson
                    accumulator[courseId] = [currentLesson];
                }

                return accumulator;
            }, {} as { [key: number]: typeof lessonList[number][] }); // The value is an array of lessons

            setLessonDictionary(lessonDictionary);

        } catch (err: any) {
            handleErrorMessage(err, setErrorMessage, history, location);
        }
    }, [setLessonDictionary, setErrorMessage, history, location]);
    const fetchDialogDictionary = React.useCallback(async () => {
        try {
            const response = await DialogApi.getAll();
            const dialogList = response.data

            const dialogDictionary = dialogList.reduce((accumulator, currentDialog) => {
                const lessonId = currentDialog.lesson_id;

                // If course_id exists in the accumulator, push the dialog into the array
                if (accumulator[lessonId]) {
                    accumulator[lessonId].push(currentDialog);
                } else {
                    // Otherwise, initialize an array with the current dialog
                    accumulator[lessonId] = [currentDialog];
                }

                return accumulator;
            }, {} as { [key: number]: typeof dialogList[number][] }); // The value is an array of lessons

            setDialogDictionary(dialogDictionary);

        } catch (err: any) {
            handleErrorMessage(err, setErrorMessage, history, location);
        }
    }, [setDialogDictionary, setErrorMessage, history, location]);
    React.useEffect(() => {
        fetchCourseList();
        fetchLessonDictionary();
        fetchDialogDictionary();
    }, [fetchCourseList, fetchLessonDictionary, fetchDialogDictionary]);

    React.useEffect(() => {
        DialogPlanApi.getById(dialogPlanId)
            .then((response) => {
                const dialogPlan = response.data;
                setId(dialogPlan.id);
                setName(dialogPlan.name);
                setDialogTitle(dialogPlan.dialog_title);
                setDialogSubtitle(dialogPlan.dialog_subtitle ? dialogPlan.dialog_subtitle : '');
                setGeneratedDialogPlanResponse(null);
                const variableList = dialogPlan.variable_list ? dialogPlan.variable_list : [];
                setVariableList(variableList)
                setCustomInputTextVariableList(variableList)
            })
            .catch(err => {
                handleErrorMessage(err, setErrorMessage, history, location);
            })
        setCustomInputMap({})
    }, [dialogPlanId, history, location, setCustomInputTextVariableList, setErrorMessage]);

    React.useEffect(() => {
        if (variableList) {
            setCustomVariableList(variableList.filter(v => v.startsWith('<CustomInput')));

            // also reset the vocaublary word list to empty
            const newVocabularyWord: IVocabularyWord = {
                id: 0,
                word: '',
                image: '',
                image_url: '',
                audio_sentence: '',
                audio_sentence_id: 0,
                dialog_prompt_id: 0,
                teacher_english_prompt: '',
                teacher_chinese_prompt: '',
                teacher_chinese_prompt_sentence_id: 0,
                student_response_prompt: '',
                student_response_prompt_sentence_id: 0,
                word_pinyin: [],
                audio_sentence_pinyin: [],
                teacher_chinese_prompt_pinyin: [],
                student_response_prompt_pinyin: [],
                word_info_text: null,
                english_text: null
            };
            const repeatedList = Array.from({ length: 10 }, (_, index) => ({
                ...newVocabularyWord,
                id: index // optional: unique ID
            }));
            setVocabularyWordList(repeatedList);

        }
    }, [variableList, setVocabularyWordList])

    React.useEffect(() => {
        // '<VocabularyWord/>' is for vocabulary word lists
        const unsupportedVariableList = variableList.filter(
            (variable) => !isSupportedVariable(variable) // Check if the variable is not in supportedVariables
        ).filter((variable) => !(variable.startsWith('<CustomInput') && variable.endsWith('/>')));

        setUnsupportedVariableList(unsupportedVariableList)
    }, [variableList, setUnsupportedVariableList])

    React.useEffect(() => {
        setCustomInputMap(prevMap => ({
            ...prevMap,
            ...customInputTextMap,
            ...customInputTextPinyinListMap
        }))
    }, [customInputTextMap, customInputTextPinyinListMap])

    const handleIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setId(Number(e.target.value));
    };

    const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setName(e.target.value);
    };

    const handleDialogTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setDialogTitle(e.target.value);
    };

    const handleDialogSubtitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setDialogSubtitle(e.target.value);
    };

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const updateDialogPlan: IDialogPlan = {
            id: id,
            name: name,
            dialog_title: dialogTitle,
            dialog_subtitle: dialogSubtitle,
        };
        onSubmit(updateDialogPlan);
    };


    const handleDeleteConfirmation = () => {
        setShowConfirmation(true);
    };

    const handleDeleteCancel = () => {
        setShowConfirmation(false);
    };

    const handleDeleteConfirm = () => {
        setShowConfirmation(false);
        onDelete(dialogPlanId);
    };

    const handleCustomInputMapChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, fieldName: string) => {
        setCustomInputMap(prevMap => ({
            ...prevMap,
            [fieldName]: e.target.value,
        }))
    };

    const formatCustomInputLabel = (variable: string) => {
        return variable
            .replace(/^<CustomInput/, '')   // remove leading <CustomInput
            .replace(/^</, '')   // remove leading <CustomInput
            .replace(/\/>$/, '')            // remove trailing />
            .replace(/TextPinyinPair/g, '')
            .replace(/([a-z])([A-Z])/g, '$1 $2')   // space before capital letter
            .replace(/([a-zA-Z])(\d+)/g, '$1 $2')  // space before numbers
            .replace(/(\d+)([a-zA-Z])/g, '$1 $2')  // space after numbers
            .trim();
    };

    const IMAGE_DELIMITER = '|--IMG--|'; // needs to match llm_chat_service.py

    const handleImageUpload = (
        e: React.ChangeEvent<HTMLInputElement>,
        variable: string,
        isList: boolean = false
    ) => {
        const files = e.target.files;
        if (!files || files.length === 0) return;

        const newImages: string[] = [];
        let filesProcessed = 0;

        Array.from(files).forEach((file) => {
            const reader = new FileReader();

            reader.onloadend = () => {
                let base64 = '';
                const result = reader.result;
                if (typeof result === 'string') base64 = result;
                else if (result instanceof ArrayBuffer) base64 = arrayBufferToBase64(result);

                newImages.push(base64);
                filesProcessed++;

                if (filesProcessed === files.length) {
                    setCustomInputMap(prev => {
                        const current = (prev[variable] && prev[variable].toString()) || '';
                        const currentImages = isList && current ? current.split(IMAGE_DELIMITER) : [];
                        const updatedImages = isList ? [...currentImages, ...newImages] : [newImages[0]];

                        return {
                            ...prev,
                            [variable]: updatedImages.join(IMAGE_DELIMITER),
                        };
                    });
                }
            };

            reader.readAsDataURL(file);
        });
    };

    const handleDeleteImageFromList = (variable: string, index: number) => {
        setCustomInputMap(prev => {
            const currentImages = (prev[variable].toString() || '').split(IMAGE_DELIMITER);
            currentImages.splice(index, 1);
            return {
                ...prev,
                [variable]: currentImages.join(IMAGE_DELIMITER),
            };
        });
    };

    const validateCustomInputMap = () => {
        const errorMap: Record<string, string> = {};

        for (const variable of variableList) {
            if (variable in customInputMap == false && (variable.startsWith('<CustomInput'))) {
                errorMap[variable] = `${formatCustomInputLabel(variable)} is required.`;
            }
            // Vocabulary inputs
            const vocabMatch = variable.match(/^<VocabularyWord(\d+)\/>$/);
            if (vocabMatch) {
                const index = parseInt(vocabMatch[1], 10) - 1;
                const word = vocabularyWordList.length > index ? vocabularyWordList[index].word : undefined;
                if (!word || word.trim() === '') {
                    errorMap[variable] = `Vocabulary Word ${index + 1} is required.`;
                }
            }
            // Vocabulary pinyin inputs
            const vocabPinyinMatch = variable.match(/^<VocabularyWordPinyin(\d+)\/>$/);
            if (vocabPinyinMatch) {
                const index = parseInt(vocabPinyinMatch[1], 10) - 1;
                const pinyin = vocabularyWordList.length > index ? getVocabularyWordPinyinMap(index, 'word_pinyin') : undefined;
                if (!pinyin || pinyin.trim() === '') {
                    errorMap[variable] = `Vocabulary Word Pinyin ${index + 1} is required.`;
                }
            }
        }
        for (const variable of customVariableList) {
            if (variable in customInputMap == false) {
                errorMap[variable] = `${formatCustomInputLabel(variable)} is required.`;
            }
            if (isCustomInputTextVariable(variable)) {
                const error = customInputTextPinyinErrorMap[variable];
                if (error != null) {
                    errorMap[variable] = error
                }
            }
        }

        for (const [key, value] of Object.entries(customInputMap)) {
            if (!value ||
                (value instanceof String && value.trim() === '') ||
                (value instanceof Array && value.length == 0)) {
                errorMap[key] = `${formatCustomInputLabel(key)} is required.`;
            }
        }

        setCustomInputMapValidation(errorMap)
        return Object.keys(errorMap).length == 0;
    };

    const handleGenerateAllDialogPlanLlmPromptInParallel = () => {
        if (validateCustomInputMap() == false) {
            return
        }
        setIsGenerating(true)
        // first generate dialog plan, without prompts, to generate things like the title and subtitle
        DialogPlanApi.generate(languageLevel, dialogPlanId, false, customInputMap,
            vocabularyWordList[0].word, vocabularyWordList[1].word, vocabularyWordList[2].word, vocabularyWordList[3].word, vocabularyWordList[4].word,
            vocabularyWordList[5].word, vocabularyWordList[6].word, vocabularyWordList[7].word, vocabularyWordList[8].word, vocabularyWordList[9].word,
            getVocabularyWordPinyinListMap(0, 'word_pinyin'), getVocabularyWordPinyinListMap(1, 'word_pinyin'),
            getVocabularyWordPinyinListMap(2, 'word_pinyin'), getVocabularyWordPinyinListMap(3, 'word_pinyin'),
            getVocabularyWordPinyinListMap(4, 'word_pinyin'), getVocabularyWordPinyinListMap(5, 'word_pinyin'),
            getVocabularyWordPinyinListMap(6, 'word_pinyin'), getVocabularyWordPinyinListMap(7, 'word_pinyin'),
            getVocabularyWordPinyinListMap(8, 'word_pinyin'), getVocabularyWordPinyinListMap(9, 'word_pinyin'))
            .then((response) => {
                const generatedDialogPlanResponse = response.data;
                generatedDialogPlanResponse.dialog_plan_llm_prompt_list = []
                generatedDialogPlanResponse.dialog_prompt_list = []
                setGeneratedDialogPlanResponse(generatedDialogPlanResponse)
                setGeneratedDialogTitle(generatedDialogPlanResponse.dialog.title)
                setGeneratedDialogSubtitle(generatedDialogPlanResponse.dialog.subtitle)

                // setIsRegenerating(
                //     new Array(generatedDialogPlanResponse.dialog_plan_llm_prompt_list.length).fill(false)
                // );
                const initialIsRegenerating = generatedDialogPlanResponse.dialog_plan_llm_prompt_list.reduce((acc, prompt) => {
                    acc[prompt.id] = false; // Initialize each prompt's isGenerating to false
                    return acc;
                }, {} as { [key: number]: boolean });

                setIsRegenerating(initialIsRegenerating);

                DialogPlanLlmPromptApi.getAll(dialogPlanId)
                    .then((response) => {
                        const dialogPlanLlmPromptList = response.data;

                        // Create an array of promises using `map` to call `generate()` for each prompt
                        const generatePromises = dialogPlanLlmPromptList.map(prompt => {

                            return DialogPlanLlmPromptApi.generateById(languageLevel, prompt.id, customInputMap,
                                vocabularyWordList[0].word, vocabularyWordList[1].word, vocabularyWordList[2].word, vocabularyWordList[3].word, vocabularyWordList[4].word,
                                vocabularyWordList[5].word, vocabularyWordList[6].word, vocabularyWordList[7].word, vocabularyWordList[8].word, vocabularyWordList[9].word,
                                getVocabularyWordPinyinListMap(0, 'word_pinyin'), getVocabularyWordPinyinListMap(1, 'word_pinyin'),
                                getVocabularyWordPinyinListMap(2, 'word_pinyin'), getVocabularyWordPinyinListMap(3, 'word_pinyin'),
                                getVocabularyWordPinyinListMap(4, 'word_pinyin'), getVocabularyWordPinyinListMap(5, 'word_pinyin'),
                                getVocabularyWordPinyinListMap(6, 'word_pinyin'), getVocabularyWordPinyinListMap(7, 'word_pinyin'),
                                getVocabularyWordPinyinListMap(8, 'word_pinyin'), getVocabularyWordPinyinListMap(9, 'word_pinyin'))
                                .then((response) => {
                                    const generatedDialogPlanLlmPromptResponse = response.data

                                    setGeneratedDialogPlanResponse((prevState) => {
                                        if (!prevState) return null; // Ensure prevState exists

                                        const updatedDialogPlanLlmPromptList = [
                                            ...prevState.dialog_plan_llm_prompt_list, // Use prevState to maintain the current state
                                            generatedDialogPlanLlmPromptResponse.dialog_plan_llm_prompt
                                        ];
                                        updatedDialogPlanLlmPromptList.sort((a, b) => a.order_index - b.order_index);

                                        const updatedDialogPromptList = [
                                            ...prevState.dialog_prompt_list, // Use prevState to maintain the current state
                                            generatedDialogPlanLlmPromptResponse.dialog_prompt
                                        ];
                                        // updatedDialogPromptList.sort((a, b) => a.order_index - b.order_index);

                                        const updatedDialogPromptConversationList = [
                                            ...prevState.dialog_prompt_conversation_list || [], // Maintain current state
                                            'dialog_prompt_conversation' in generatedDialogPlanLlmPromptResponse
                                                ? generatedDialogPlanLlmPromptResponse.dialog_prompt_conversation
                                                : null // Include `null` if `dialog_prompt_conversation` doesn't exist
                                        ];

                                        // Sort both lists based on `dialog_prompt_list.order_index`
                                        const sortedIndices = updatedDialogPromptList
                                            .map((item, index) => ({ index, order_index: item.order_index })) // Map items to their indices
                                            .sort((a, b) => a.order_index - b.order_index) // Sort by `order_index`
                                            .map((item) => item.index); // Get the sorted indices

                                        const sortedDialogPromptList = sortedIndices.map((i) => updatedDialogPromptList[i]);
                                        const sortedDialogPromptConversationList = sortedIndices.map((i) => updatedDialogPromptConversationList[i]);


                                        // Update the state by spreading prevState and updating the arrays
                                        return {
                                            ...prevState,
                                            dialog_plan_llm_prompt_list: updatedDialogPlanLlmPromptList,
                                            dialog_prompt_list: sortedDialogPromptList,
                                            dialog_prompt_conversation_list: sortedDialogPromptConversationList
                                        };
                                    });
                                })
                                .catch(err => {
                                    handleErrorMessage(err, setErrorMessage, history, location);
                                    showErrorDialog(err);
                                });
                        });

                        // Execute all the `generate()` calls concurrently
                        return Promise.all(generatePromises)
                            .finally(() => { setIsGenerating(false) });
                    })
                    .catch(err => {
                        setIsGenerating(false);
                        handleErrorMessage(err, setErrorMessage, history, location);
                        showErrorDialog(err);
                    })
            })
            .catch(err => {
                setIsGenerating(false);
                handleErrorMessage(err, setErrorMessage, history, location);
                showErrorDialog(err);
            })
    }

    React.useEffect(() => {
        // Check if scrolling hasn't happened yet and list has at least one element
        if (
            generatedDialogPlanResponse &&
            generatedDialogPlanResponse.dialog_plan_llm_prompt_list.length === 1 &&
            !hasScrolledToGeneratedPrompt.current
        ) {
            const element = document.getElementById('form-0'); // Scroll to the first element
            if (element) {
                element.scrollIntoView({ behavior: 'smooth' });
                hasScrolledToGeneratedPrompt.current = true; // Mark scrolling as done
            }
        }
    }, [generatedDialogPlanResponse]);

    const handleLlmPromptChange = (updatedDialogPlanLlmPrompt: DialogPlanLlmPrompt) => {
        setGeneratedDialogPlanResponse((prevState) => {
            if (!prevState) return prevState;

            // Map over the list and replace the entire object where the ID matches
            const updatedDialogPlanLlmPromptList = prevState.dialog_plan_llm_prompt_list.map((prompt) =>
                prompt.id === updatedDialogPlanLlmPrompt.id
                    ? updatedDialogPlanLlmPrompt // Replace the entire object
                    : prompt // Keep the other prompts unchanged
            );

            // Return the updated state with the modified prompt list
            return {
                ...prevState,
                dialog_plan_llm_prompt_list: updatedDialogPlanLlmPromptList,
            };
        });
    }

    const handleRegenerateDialogPlanLlmPrompt = (dialogPlanLlmPrompt: DialogPlanLlmPrompt, fieldName: string) => {
        // Set isGenerating true for this specific dialogPlanLlmPrompt.id
        setIsRegenerating((prevState) => ({
            ...prevState,
            [dialogPlanLlmPrompt.id]: true
        }));

        if (!generatedDialogPlanResponse) throw Error("generatedDialogPlanResponse is still empty. please generate")

        // Find the index of the dialogPlanLlmPrompt in dialog_plan_llm_prompt_list
        const promptIndex = generatedDialogPlanResponse.dialog_plan_llm_prompt_list.findIndex(
            (prompt) => prompt.id === dialogPlanLlmPrompt.id
        );

        // If the index is found, return the corresponding dialog_prompt
        if (promptIndex < 0) {
            throw Error(`Unable to find dialogPlanLlmPrompt id=${dialogPlanLlmPrompt.id} in generatedDialogPlanResponse`)
        }

        const dialogPrompt = generatedDialogPlanResponse.dialog_prompt_list[promptIndex];

        DialogPlanLlmPromptApi.generate(languageLevel, dialogPlanLlmPrompt, dialogPrompt, fieldName, customInputMap,
            getEffectiveVocabularyWord(vocabularyWordList, dialogPrompt, 0), getEffectiveVocabularyWord(vocabularyWordList, dialogPrompt, 1),
            getEffectiveVocabularyWord(vocabularyWordList, dialogPrompt, 2), getEffectiveVocabularyWord(vocabularyWordList, dialogPrompt, 3),
            getEffectiveVocabularyWord(vocabularyWordList, dialogPrompt, 4), getEffectiveVocabularyWord(vocabularyWordList, dialogPrompt, 5),
            getEffectiveVocabularyWord(vocabularyWordList, dialogPrompt, 6), getEffectiveVocabularyWord(vocabularyWordList, dialogPrompt, 7),
            getEffectiveVocabularyWord(vocabularyWordList, dialogPrompt, 8), getEffectiveVocabularyWord(vocabularyWordList, dialogPrompt, 9),
            getEffectiveVocabularyWordPinyinList(vocabularyWordList, dialogPrompt, getVocabularyWordPinyinListMap, 0),
            getEffectiveVocabularyWordPinyinList(vocabularyWordList, dialogPrompt, getVocabularyWordPinyinListMap, 1),
            getEffectiveVocabularyWordPinyinList(vocabularyWordList, dialogPrompt, getVocabularyWordPinyinListMap, 2),
            getEffectiveVocabularyWordPinyinList(vocabularyWordList, dialogPrompt, getVocabularyWordPinyinListMap, 3),
            getEffectiveVocabularyWordPinyinList(vocabularyWordList, dialogPrompt, getVocabularyWordPinyinListMap, 4),
            getEffectiveVocabularyWordPinyinList(vocabularyWordList, dialogPrompt, getVocabularyWordPinyinListMap, 5),
            getEffectiveVocabularyWordPinyinList(vocabularyWordList, dialogPrompt, getVocabularyWordPinyinListMap, 6),
            getEffectiveVocabularyWordPinyinList(vocabularyWordList, dialogPrompt, getVocabularyWordPinyinListMap, 7),
            getEffectiveVocabularyWordPinyinList(vocabularyWordList, dialogPrompt, getVocabularyWordPinyinListMap, 8),
            getEffectiveVocabularyWordPinyinList(vocabularyWordList, dialogPrompt, getVocabularyWordPinyinListMap, 9))
            .then((response) => {
                const generatedDialogPlanLlmPromptResponse = response.data

                setGeneratedDialogPlanResponse((prevState) => {
                    if (!prevState) return null; // Ensure prevState exists

                    // Find the index of the dialogPlanLlmPrompt that should be updated
                    const promptIndex = prevState.dialog_plan_llm_prompt_list.findIndex(
                        (prompt) => prompt.id === generatedDialogPlanLlmPromptResponse.dialog_plan_llm_prompt.id
                    );

                    if (promptIndex === -1) {
                        // If no matching prompt is found, return the previous state
                        return prevState;
                    }

                    // Create a copy of the dialog_plan_llm_prompt_list and update the item at the found index
                    const updatedDialogPlanLlmPromptList = [...prevState.dialog_plan_llm_prompt_list];

                    if (fieldName) {
                        // Update only the specified field, Keep other fields unchanged
                        updatedDialogPlanLlmPromptList[promptIndex] = {
                            ...updatedDialogPlanLlmPromptList[promptIndex],
                            // @ts-expect-error dynamic field update from API payload
                            [fieldName]: generatedDialogPlanLlmPromptResponse.dialog_plan_llm_prompt[fieldName],
                        };
                    } else {
                        updatedDialogPlanLlmPromptList[promptIndex] = generatedDialogPlanLlmPromptResponse.dialog_plan_llm_prompt;
                    }

                    // Sort the updated list by order_index
                    updatedDialogPlanLlmPromptList.sort((a, b) => a.order_index - b.order_index);

                    // Create a copy of the dialog_prompt_list and update the item at the same index
                    const updatedDialogPromptList = [...prevState.dialog_prompt_list];

                    if (fieldName === "wrong_word_list") {
                        const currentPrompt = updatedDialogPromptList[promptIndex] as DialogPromptQuizWord;
                        const generatedPrompt = generatedDialogPlanLlmPromptResponse.dialog_prompt as DialogPromptQuizWord;
                        updatedDialogPromptList[promptIndex] = {
                            ...currentPrompt,
                            wrong_word_1: generatedPrompt.wrong_word_1,
                            wrong_word_2: generatedPrompt.wrong_word_2,
                            wrong_word_3: generatedPrompt.wrong_word_3,
                            wrong_word_4: generatedPrompt.wrong_word_4,
                            wrong_word_5: generatedPrompt.wrong_word_5,
                        } as DialogPromptQuizWord;
                    } else if (fieldName) {
                        // Update only the specified field, Keep other fields unchanged
                        updatedDialogPromptList[promptIndex] = mergeGeneratedDialogPromptField(
                            updatedDialogPromptList[promptIndex],
                            generatedDialogPlanLlmPromptResponse.dialog_prompt,
                            fieldName,
                        );
                    } else {
                        updatedDialogPromptList[promptIndex] = generatedDialogPlanLlmPromptResponse.dialog_prompt
                    }

                    const updatedDialogPromptConversationList = [...prevState.dialog_prompt_conversation_list];
                    if (fieldName == 'dialog_prompt_conversation') {
                        updatedDialogPromptConversationList[promptIndex] = generatedDialogPlanLlmPromptResponse.dialog_prompt_conversation
                    } else if (fieldName) {
                        // not dialog_prompt_conversation field, do nothing
                    } else {
                        updatedDialogPromptConversationList[promptIndex] = generatedDialogPlanLlmPromptResponse.dialog_prompt_conversation
                    }

                    // // Sort the updated dialog prompt list by order_index (if needed)
                    // updatedDialogPromptList.sort((a, b) => a.order_index - b.order_index);

                    // Sort both lists based on `dialog_prompt_list.order_index`
                    const sortedIndices = updatedDialogPromptList
                        .map((item, index) => ({ index, order_index: item.order_index })) // Map items to their indices
                        .sort((a, b) => a.order_index - b.order_index) // Sort by `order_index`
                        .map((item) => item.index); // Get the sorted indices

                    const sortedDialogPromptList = sortedIndices.map((i) => updatedDialogPromptList[i]);
                    const sortedDialogPromptConversationList = sortedIndices.map((i) => updatedDialogPromptConversationList[i]);

                    // Update the state by spreading prevState and updating the arrays
                    return {
                        ...prevState,
                        dialog_plan_llm_prompt_list: updatedDialogPlanLlmPromptList,
                        dialog_prompt_list: sortedDialogPromptList,
                        dialog_prompt_conversation_list: sortedDialogPromptConversationList,
                    };
                });
            })
            .catch(err => {
                handleErrorMessage(err, setErrorMessage, history, location);
                showErrorDialog(err);
            })
            .finally(() => {
                // Set isGenerating true for this specific dialogPlanLlmPrompt.id
                setIsRegenerating((prevState) => ({
                    ...prevState,
                    [dialogPlanLlmPrompt.id]: false
                }));
            })
    }

    // Handle the `onChange` event to update the temporary value
    const handleGeneratedDialogTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setGeneratedDialogTitle(e.target.value); // Keep track of the text as the user types
    };
    const handleGeneratedDialogTitleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        const newTitle = e.target.value; // Get the new value from the input

        setGeneratedDialogPlanResponse(prevState => {
            if (prevState) {
                return {
                    ...prevState,
                    dialog: {
                        ...prevState.dialog,
                        dialogTitle: newTitle // Update the specific field with the new title
                    }
                };
            } else {
                return prevState;
            }
        });
    };

    // Handle the `onChange` event to update the temporary value
    const handleGeneratedDialogSubtitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setGeneratedDialogSubtitle(e.target.value); // Keep track of the text as the user types
    };
    const handleGeneratedDialogSubtitleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        const newSubtitle = e.target.value; // Get the new value from the input

        setGeneratedDialogPlanResponse(prevState => {
            if (prevState) {
                return {
                    ...prevState,
                    dialog: {
                        ...prevState.dialog,
                        dialogSubtitle: newSubtitle // Update the specific field with the new subtitle
                    }
                };
            } else {
                return prevState;
            }
        });
    };

    const [isInsertDialogPromptValid, setIsInsertDialogPromptValid] = React.useState(true); // Track validation state
    const handleSelectChange = (e: SelectChangeEvent<string>) => {
        setInsertLessonAndDialogId(e.target.value);
        setIsInsertDialogPromptValid(true); // Reset the validation state when user selects an option
    };

    const handleInsertDialogPrompt = () => {
        if (!insertLessonAndDialogId) {
            setIsInsertDialogPromptValid(false); // Mark as invalid if nothing is selected
            return;
        }
        if (validateCustomInputMap() == false) {
            return;
        }
        if (insertLessonAndDialogId && generatedDialogPlanResponse) {
            setIsInserting(true)
            // Split the string using '-' as the separator
            const [lessonIdString, dialogIdString] = insertLessonAndDialogId.split('-');
            const insertLessonId = lessonIdString ? parseInt(lessonIdString, 10) : null;
            const insertDialogId = dialogIdString ? parseInt(dialogIdString, 10) : null;

            if (!insertLessonId) {
                throw Error("invalid lesson dialog id string '" + insertLessonAndDialogId + "'")
            }
            let orderIndex: number | null = null;
            if (!(insertLessonId in dialogDictionary) || dialogDictionary[insertLessonId].length == 0) {
                orderIndex = 1;
            } else {
                const dialogList = dialogDictionary[insertLessonId];

                if (insertDialogId == null) {
                    orderIndex = dialogList[0].order_index - 1;
                } else if (dialogList[0]) {
                    let beforeOrderIndex: number | null = null;
                    let afterOrderIndex: number | null = null;
                    for (let i = 0; i < dialogList.length; i++) {
                        beforeOrderIndex = dialogList[i].order_index;
                        if (i + 1 < dialogList.length) {
                            afterOrderIndex = dialogList[i + 1].order_index;
                        } else {
                            afterOrderIndex = null;
                        }
                        if (dialogList[i].id === insertDialogId) {
                            break;
                        }
                    }

                    if (beforeOrderIndex != null && afterOrderIndex != null) {
                        orderIndex = (beforeOrderIndex + afterOrderIndex) / 2
                    } else if (afterOrderIndex == null && beforeOrderIndex != null) {
                        orderIndex = beforeOrderIndex + 1;
                    } else {
                        throw Error("handleInsertDialogPrompt() has a bug of some kind")
                    }
                }
            }
            const newDialog: IDialog = {
                id: 0,
                lesson_id: insertLessonId,
                order_index: orderIndex ? orderIndex : 0,
                title: generatedDialogTitle,
                subtitle: generatedDialogSubtitle,
                is_hidden: false
            }
            DialogApi.create(newDialog)
                .then(response => {
                    if (response.status < 300) {
                        return response.data
                    }
                    console.error(response)
                    handleErrorMessage(response, setErrorMessage, history, location);
                    return null
                })
                .then(async newDialogWithId => {
                    fetchDialogDictionary()
                    if (newDialogWithId) {
                        setInsertLessonAndDialogId(`${insertLessonId}-${newDialogWithId.id}`);
                        const promises = [];
                        for (let i = 0; i < generatedDialogPlanResponse.dialog_prompt_list.length; i++) {
                            const dialogPrompt = generatedDialogPlanResponse.dialog_prompt_list[i]
                            const dialogPromptConversation = generatedDialogPlanResponse.dialog_prompt_conversation_list[i]
                            if (dialogPromptConversation) {
                                const conversationWithId = await DialogPromptConversationApi.create(dialogPromptConversation);
                                if (conversationWithId.status >= 300) {
                                    throw `Creating conversation return http status ${conversationWithId.status}`
                                }
                                if ('dialog_prompt_conversation_id' in dialogPrompt) {
                                    dialogPrompt.dialog_prompt_conversation_id = conversationWithId.data.id
                                } else {
                                    throw `conversation object, but prompt ${dialogPrompt.id} class ${dialogPrompt.class_name} doesn't have conversation_id field`
                                }
                            }
                            dialogPrompt.dialog_id = newDialogWithId.id;
                            const promise = DialogPromptApi.create(dialogPrompt)
                                .then(response => {
                                    if (response.status < 300) {
                                        return response.data
                                    }
                                    console.error(response)
                                    handleErrorMessage(response, setErrorMessage, history, location);
                                    return null
                                })
                                .then(() => {
                                    // remove only this specific dialog prompt from list
                                    // IMPORTANT: remove by identity, not by original `i`
                                    setGeneratedDialogPlanResponse(function (prevState) {
                                        if (!prevState) return null;

                                        let idx = -1;
                                        for (let j = 0; j < prevState.dialog_prompt_list.length; j++) {
                                            if (prevState.dialog_prompt_list[j] === dialogPrompt) {
                                                idx = j;
                                                break;
                                            }
                                        }

                                        // If we can't find it, just return the previous state
                                        if (idx === -1) {
                                            console.warn("Could not find dialogPrompt to remove");
                                            return prevState;
                                        }

                                        // Copy all three arrays
                                        const updatedDialogPlanLlmPromptList = prevState.dialog_plan_llm_prompt_list
                                            ? prevState.dialog_plan_llm_prompt_list.slice()
                                            : [];
                                        const updatedDialogPromptList = prevState.dialog_prompt_list.slice();
                                        const updatedDialogPromptConversationList = prevState.dialog_prompt_conversation_list
                                            ? prevState.dialog_prompt_conversation_list.slice()
                                            : [];

                                        // Guard against length mismatch
                                        if (idx < updatedDialogPlanLlmPromptList.length) {
                                            updatedDialogPlanLlmPromptList.splice(idx, 1);
                                        }
                                        if (idx < updatedDialogPromptList.length) {
                                            updatedDialogPromptList.splice(idx, 1);
                                        }
                                        if (idx < updatedDialogPromptConversationList.length) {
                                            updatedDialogPromptConversationList.splice(idx, 1);
                                        }

                                        return {
                                            ...prevState,
                                            dialog_plan_llm_prompt_list: updatedDialogPlanLlmPromptList,
                                            dialog_prompt_list: updatedDialogPromptList,
                                            dialog_prompt_conversation_list: updatedDialogPromptConversationList,
                                        };
                                    });
                                })
                                .catch(err => {
                                    handleErrorMessage(err, setErrorMessage, history, location);
                                    showErrorDialog(err);
                                })
                            promises.push(promise);
                        }
                        // Wait for all promises to settle (either resolved or rejected)
                        Promise.allSettled(promises)
                            .then(() => {
                                // After all of the API calls have completed, set isInserting to false
                                setIsInserting(false);
                                // history.push(`/create_lesson?dialogId=${newDialogWithId.id}`)
                                window.open(`/create_lesson?dialogId=${newDialogWithId.id}`, "_blank");
                            });
                    } else {
                        setIsInserting(false);
                        console.error("response data is empty, newDialogWithId is empty");
                        setErrorMessage("response data is empty, newDialogWithId is empty");
                    }
                })
                .catch(err => {
                    setIsInserting(false)
                    handleErrorMessage(err, setErrorMessage, history, location);
                    showErrorDialog(err);

                })
        }
    }

    const handleUpdateDialogPrompt = async () => {
        if (!insertLessonAndDialogId) {
            setIsInsertDialogPromptValid(false); // Mark as invalid if nothing is selected
            return;
        }
        if (validateCustomInputMap() == false) {
            return;
        }
        if (insertLessonAndDialogId && generatedDialogPlanResponse) {
            setIsInserting(true)
            // Split the string using '-' as the separator
            const [, dialogIdString] = insertLessonAndDialogId.split('-');
            const updateDialogId = dialogIdString ? parseInt(dialogIdString, 10) : null;

            if (!updateDialogId) {
                throw Error("invalid lesson dialog id string '" + insertLessonAndDialogId + "'")
            }

            const promises = [];
            for (let i = 0; i < generatedDialogPlanResponse.dialog_prompt_list.length; i++) {
                const dialogPrompt = generatedDialogPlanResponse.dialog_prompt_list[i]
                const dialogPromptConversation = generatedDialogPlanResponse.dialog_prompt_conversation_list[i]
                if (dialogPromptConversation) {
                    const conversationWithId = await DialogPromptConversationApi.create(dialogPromptConversation);
                    if (conversationWithId.status >= 300) {
                        throw `Creating conversation return http status ${conversationWithId.status}`
                    }
                    if ('dialog_prompt_conversation_id' in dialogPrompt) {
                        dialogPrompt.dialog_prompt_conversation_id = conversationWithId.data.id
                    } else {
                        throw `conversation object, but prompt ${dialogPrompt.id} class ${dialogPrompt.class_name} doesn't have conversation_id field`
                    }
                }
                dialogPrompt.dialog_id = updateDialogId;
                const promise = DialogPromptApi.create(dialogPrompt)
                    .then(response => {
                        if (response.status < 300) {
                            return response.data
                        }
                        console.error(response)
                        handleErrorMessage(response, setErrorMessage, history, location);
                        return null
                    })
                    .then(() => {
                        // remove only this specific dialog prompt from list
                        // IMPORTANT: remove by identity, not by original `i`
                        setGeneratedDialogPlanResponse(function (prevState) {
                            if (!prevState) return null;

                            let idx = -1;
                            for (let j = 0; j < prevState.dialog_prompt_list.length; j++) {
                                if (prevState.dialog_prompt_list[j] === dialogPrompt) {
                                    idx = j;
                                    break;
                                }
                            }

                            // If we can't find it, just return the previous state
                            if (idx === -1) {
                                console.warn("Could not find dialogPrompt to remove");
                                return prevState;
                            }

                            // Copy all three arrays
                            const updatedDialogPlanLlmPromptList = prevState.dialog_plan_llm_prompt_list
                                ? prevState.dialog_plan_llm_prompt_list.slice()
                                : [];
                            const updatedDialogPromptList = prevState.dialog_prompt_list.slice();
                            const updatedDialogPromptConversationList = prevState.dialog_prompt_conversation_list
                                ? prevState.dialog_prompt_conversation_list.slice()
                                : [];

                            // Guard against length mismatch
                            if (idx < updatedDialogPlanLlmPromptList.length) {
                                updatedDialogPlanLlmPromptList.splice(idx, 1);
                            }
                            if (idx < updatedDialogPromptList.length) {
                                updatedDialogPromptList.splice(idx, 1);
                            }
                            if (idx < updatedDialogPromptConversationList.length) {
                                updatedDialogPromptConversationList.splice(idx, 1);
                            }

                            return {
                                ...prevState,
                                dialog_plan_llm_prompt_list: updatedDialogPlanLlmPromptList,
                                dialog_prompt_list: updatedDialogPromptList,
                                dialog_prompt_conversation_list: updatedDialogPromptConversationList,
                            };
                        });
                    })
                    .catch(err => {
                        handleErrorMessage(err, setErrorMessage, history, location);
                        showErrorDialog(err);
                    })
                promises.push(promise);
            }
            // Wait for all promises to settle (either resolved or rejected)
            Promise.allSettled(promises)
                .then(() => {
                    // After all of the API calls have completed, set isInserting to false
                    setIsInserting(false);
                    // history.push(`/create_lesson?dialogId=${newDialogWithId.id}`)
                    window.open(`/create_lesson?dialogId=${updateDialogId}`, "_blank");
                });
        }
    }

    return (<>
        <form onSubmit={handleSubmit}>
            <Stack spacing={4} sx={{ width: '90%', margin: '0 auto', pt: 2, pb: 4 }}>
                <Typography variant="h4">
                    Edit Dialog Plan
                </Typography>
                <input type="hidden" value={id} onChange={handleIdChange} />
                <TextField
                    label="Name"
                    value={name}
                    onChange={handleNameChange}
                    required
                />
                <div>
                    <Typography sx={{ color: 'grey.500', fontSize: '0.9rem' }}>
                        &lt;SkipLLM/&gt; will take the text verbatim and not ask the LLM to generate anything
                    </Typography>
                    <Typography sx={{ color: 'grey.500', fontSize: '0.9rem' }}>
                        &lt;VocabularyWord1/&gt; will be replaced with Vocabulary Word Input when generating. <br />
                        Also &lt;VocabularyWord2/&gt;, &lt;VocabularyWord3/&gt;, ... ,  &lt;VocabularyWord10/&gt;
                    </Typography>
                    <Typography sx={{ color: 'grey.500', fontSize: '0.9rem' }}>
                        &lt;VocabularyWord1Pinyin/&gt; will be replaced with Vocabulary Word Pinyin Input when generating. <br />
                        Also &lt;VocabularyWord2Pinyin/&gt;, &lt;VocabularyWord3Pinyin/&gt;, ... ,  &lt;VocabularyWord10Pinyin/&gt;
                    </Typography>
                    <Typography sx={{ color: 'grey.500', fontSize: '0.9rem' }}>
                        &lt;CustomInput***/&gt; can be used for additional inputs
                    </Typography>
                    <Typography sx={{ color: 'grey.650', fontSize: '0.9rem' }}>
                        &lt;CustomInput***TextPinyinPair/&gt; can be used for word / pinyin pair of fields
                    </Typography>
                    <Typography sx={{ color: 'grey.500', fontSize: '0.9rem' }}>
                        &lt;CustomInput***Image/&gt; can be used for single image input
                    </Typography>
                    <Typography sx={{ color: 'grey.500', fontSize: '0.9rem' }}>
                        &lt;CustomInput***ImageList/&gt; can be used for multiple image input
                    </Typography>
                    {/* <Typography sx={{ color: 'grey.500', fontSize: '0.9rem' }}>
                        &lt;HskLevel/&gt; will be replaced with the number only, i.e. 1, 2, 3, 4, 5, 6
                    </Typography> */}
                    <Typography sx={{ color: 'grey.500', fontSize: '0.9rem' }}>
                        &lt;StudentName/&gt; will be replaced with the student's name
                    </Typography>
                    <Typography sx={{ color: 'grey.500', fontSize: '0.9rem' }}>
                        &lt;TeacherName/&gt; will be replaced with the teacher's name
                    </Typography>
                    <Typography sx={{ color: 'grey.500', fontSize: '0.9rem' }}>
                        &lt;Microphone/&gt; will be replaced with the microphone icon for recording
                    </Typography>
                    <Typography sx={{ color: 'grey.500', fontSize: '0.9rem' }}>
                        &lt;Speaker/&gt; will be replaced with the speaker icon for listening
                    </Typography>
                    <Typography sx={{ color: 'grey.500', fontSize: '0.9rem' }}>
                        &lt;Info/&gt; will be replaced with the info icon for more details
                    </Typography>
                    <Typography sx={{ color: 'grey.500', fontSize: '0.9rem' }}>
                        &lt;Feedback/&gt; will be replaced with the feedback button icon
                    </Typography>
                </div>
                <TextField
                    label="Dialog Title"
                    value={dialogTitle}
                    onChange={handleDialogTitleChange}
                    required
                />
                <TextField
                    label="Dialog Subtitle"
                    value={dialogSubtitle}
                    onChange={handleDialogSubtitleChange}
                />
                <Button type="submit" variant="contained">
                    Save
                </Button>
                <Button variant="contained" color="error" onClick={handleDeleteConfirmation}>
                    Delete
                </Button>
                <Dialog open={showConfirmation} onClose={handleDeleteCancel}>
                    <DialogTitle><Typography variant="h4" sx={{ color: '#383838' }}>Delete Dialog Plan</Typography></DialogTitle>
                    <DialogContent>
                        <Typography sx={{ color: '#8D8D8D' }}>Are you sure you want to delete this Dialog Plan?</Typography>
                        <Typography>{name} :: {dialogTitle}</Typography>
                    </DialogContent>
                    <DialogActions sx={{ justifyContent: 'space-between', '& .MuiButton-root': { flex: 1 } }}>
                        <Button onClick={handleDeleteCancel} sx={{ color: '#787878' }}>Cancel</Button>
                        <Button onClick={handleDeleteConfirm} sx={{ color: '#787878' }} autoFocus>Delete</Button>
                    </DialogActions>
                </Dialog>
            </Stack>
        </form>
        <Stack spacing={4} sx={{ width: '90%', margin: '0 auto', pt: 2, pb: 4 }}>
            <Typography variant="h4">
                Generate Dialog Plan
            </Typography>
            {/* Dropdown for Language Level */}
            <Select
                labelId="language-level-label"
                id="language-level-select"
                value={languageLevel}
                label="Language Level"
                onChange={(event) => setLanguageLevel(event.target.value as LanguageLevelEnum)}
            >
                {Object.values(LanguageLevelEnum).map((level) => (
                    <MenuItem key={level} value={level}>
                        {LanguageLevelLabels[level]}
                    </MenuItem>
                ))}
            </Select>
            {unsupportedVariableList.length > 0 && (
                <div>
                    <Typography variant="body1" sx={{ color: 'red' }}>
                        The following variables are not supported:
                    </Typography>
                    {unsupportedVariableList.map((variable, index) => (
                        <Typography key={index} variant="body2" sx={{ color: 'red' }}>
                            {variable}
                        </Typography>
                    ))}
                </div>
            )}
            {/* {vocabularyWordList.map((item, index) => {
                const labelSuffix = index + 1;
                const tag = `<VocabularyWord${labelSuffix}/>`;
                if (!variableList.includes(tag)) return null;

                return (<Stack direction="column" sx={{ width: '100%' }}>
                    <TextField
                        label={`Vocabulary Word ${labelSuffix}`}
                        value={item.word}
                        onChange={(e) => handleVocabularyWordTextChange(index, 'word', e.target.value)}
                        multiline
                        minRows={1}
                        sx={{ '& textarea': { resize: 'vertical' } }}
                    />
                    <TextField
                        label={`Vocabulary Word ${labelSuffix} Pinyin`}
                        value={getVocabularyWordPinyinMap(index, 'word_pinyin')}
                        onChange={(e) => handleVocabularyWordPinyinChange(index, 'word_pinyin', e.target.value)}
                        error={getVocabularyWordErrorMap(index, 'word_pinyin') != null}
                        helperText={getVocabularyWordErrorMap(index, 'word_pinyin')}
                        multiline
                        minRows={1}
                        sx={{ mt: 1, '& textarea': { resize: 'vertical' } }}
                    />
                </Stack>
                );
            })} */}

            {variableList.map((variable) => {
                const lower = variable.toLowerCase();
                const isImageList = lower.endsWith("imagelist/>");
                const isImage = !isImageList && lower.endsWith("image/>");

                const isTextPinyinPairVariable = isCustomInputTextVariable(variable)

                const isCustomVariable = customVariableList.includes(variable);

                const match = lower.match(/^<vocabularyword(\d+)\/>$/)
                const isVocabularyWord = match != null
                const vocabularyWordNumber = match ? parseInt(match[1]) : 0
                const vocabularyWordIndex = vocabularyWordNumber - 1

                return (isTextPinyinPairVariable) ? (
                    <Stack direction="column" sx={{ width: '100%', pb: 2 }}>
                        <TextField
                            label={formatCustomInputLabel(variable)}
                            value={customInputTextMap[variable]}
                            onChange={(e) => handleCustomInputTextChange(variable, e.target.value)}
                            multiline
                            minRows={1}
                            sx={{ '& textarea': { resize: 'vertical' } }}
                        />
                        <TextField
                            label={`${formatCustomInputLabel(variable)} Pinyin`}
                            value={customInputTextPinyinMap[variable]}
                            onChange={(e) => handleCustomInputTextPinyinChange(variable, e.target.value)}
                            error={customInputTextPinyinErrorMap[variable] != null}
                            helperText={customInputTextPinyinErrorMap[variable]}
                            multiline
                            minRows={1}
                            sx={{ mt: 1, '& textarea': { resize: 'vertical' } }}
                        />
                    </Stack>
                ) : (isImage || isImageList) ? (
                    <Stack direction="column" sx={{ width: '100%', pb: 2 }}>
                        <InputLabel htmlFor={`upload-${variable}`}>
                            {formatCustomInputLabel(variable)}
                        </InputLabel>
                        <Input
                            type="file"
                            id={`upload-${variable}`}
                            inputProps={{
                                accept: "image/*",
                                multiple: isImageList, // allow multiple selection only for ImageList
                            }}
                            onChange={(e) =>
                                handleImageUpload(
                                    e as React.ChangeEvent<HTMLInputElement>,
                                    variable,
                                    isImageList // pass true if it’s a list
                                )
                            }
                        />

                        {/* Display image(s) */}
                        {((customInputMap[variable] && customInputMap[variable].toString()) || '')
                            .split(IMAGE_DELIMITER)
                            .filter(src => src) // filter out empty strings
                            .map((src, index) => (
                                <Box key={index} sx={{ position: 'relative', display: 'inline-block', mr: 2, mt: 1 }}>
                                    <img
                                        src={src}
                                        alt={`Image ${index}`}
                                        style={{ maxWidth: '120px', maxHeight: '120px' }}
                                    />
                                    <Button
                                        size="small"
                                        onClick={() => handleDeleteImageFromList(variable, index)}
                                        sx={{
                                            position: 'absolute',
                                            top: 0,
                                            right: 0,
                                            backgroundColor: 'white',
                                            borderRadius: '50%',
                                            minWidth: '28px',
                                            height: '28px',
                                            boxShadow: 1,
                                            p: 0.5,
                                            '&:hover': {
                                                backgroundColor: '#f8f8f8',
                                            },
                                        }}
                                    >
                                        <DeleteRoundedIcon sx={{ color: 'red', fontSize: '20px' }} />
                                    </Button>
                                </Box>
                            ))}
                    </Stack>
                ) : (isCustomVariable ? (
                    <TextField
                        label={formatCustomInputLabel(variable)}
                        value={customInputMap[variable] || ''}
                        onChange={(e) => handleCustomInputMapChange(e, variable)}
                        fullWidth
                        margin="normal"
                        multiline
                        minRows={1}
                        sx={{ '& textarea': { resize: 'vertical' }, pb: 2 }}
                    />
                ) : (isVocabularyWord) ? (
                    <Stack direction="column" sx={{ width: '100%', pb: 2 }}>
                        <TextField
                            label={`Vocabulary Word ${vocabularyWordNumber}`}
                            value={vocabularyWordList[vocabularyWordIndex].word}
                            onChange={(e) => handleVocabularyWordTextChange(vocabularyWordIndex, 'word', e.target.value)}
                            multiline
                            minRows={1}
                            sx={{ '& textarea': { resize: 'vertical' } }}
                        />
                        <TextField
                            label={`Vocabulary Word ${vocabularyWordNumber} Pinyin`}
                            value={getVocabularyWordPinyinMap(vocabularyWordIndex, 'word_pinyin')}
                            onChange={(e) => handleVocabularyWordPinyinChange(vocabularyWordIndex, 'word_pinyin', e.target.value)}
                            error={getVocabularyWordErrorMap(vocabularyWordIndex, 'word_pinyin') != null}
                            helperText={getVocabularyWordErrorMap(vocabularyWordIndex, 'word_pinyin')}
                            multiline
                            minRows={1}
                            sx={{ mt: 1, '& textarea': { resize: 'vertical' } }}
                        />
                    </Stack>
                ) : (isSupportedVariable(variable)) ? null : (
                    <>ERROR Unknown variable name {variable}</>
                ))
            })}

            {Object.entries(customInputMapValidation).length > 0 &&
                <ul>
                    {Object.entries(customInputMapValidation).map(([key, message]) => (
                        <li key={key}>
                            <Typography color="error" variant="body2">{message}</Typography>
                        </li>
                    ))}
                </ul>
            }
            <Button variant="contained" onClick={handleGenerateAllDialogPlanLlmPromptInParallel} disabled={isGenerating}>
                Generate
                {isGenerating && <CircularProgress size={16} sx={{ ml: 2 }} />}
            </Button>
            {generatedDialogPlanResponse != null && (<>

                {generatedDialogPlanResponse.dialog_plan_llm_prompt_list.map((llm_prompt, index) => (
                    <div style={{ display: 'flex' }} key={`wrapper-${index}`}> {/* Flex container */}
                        <div id={`form-${index}`} style={{ flex: 1, marginRight: '16px' }}> {/* Flex child */}
                            <DialogPlanLlmPromptForm
                                originalDialogPlanLlmPrompt={llm_prompt}
                                handleLlmPromptChange={handleLlmPromptChange}
                                handleRegenerateDialogPlanLlmPrompt={handleRegenerateDialogPlanLlmPrompt}
                                isRegenerating={isRegenerating[llm_prompt.id] || false} />
                        </div>
                        <div id={`response-${index}`} style={{ flex: 1 }}> {/* Flex child */}
                            <DialogPlanLlmPromptGenerated
                                dialogPrompt={generatedDialogPlanResponse.dialog_prompt_list[index]}
                                dialogPromptConversation={generatedDialogPlanResponse.dialog_prompt_conversation_list[index]}
                                handleLlmPromptChange={handleLlmPromptChange}
                                handleRegenerateDialogPlanLlmPrompt={handleRegenerateDialogPlanLlmPrompt}
                                isRegenerating={isRegenerating[llm_prompt.id] || false} />
                        </div>
                    </div>
                ))}

                <Divider sx={{ borderColor: '#FFBE19', borderWidth: '2px', mb: 2 }} />  {/* Customized Divider */}

                <Typography variant="h4">
                    Insert New Dialog Prompt into Dialog
                </Typography>

                <TextField
                    label="Generated Dialog Title"
                    value={generatedDialogTitle}
                    onChange={handleGeneratedDialogTitleChange} // Track changes while typing
                    onBlur={handleGeneratedDialogTitleBlur} // Update global state on blur
                />

                <TextField
                    label="Generated Dialog Subtitle"
                    value={generatedDialogSubtitle}
                    onChange={handleGeneratedDialogSubtitleChange} // Track changes while typing
                    onBlur={handleGeneratedDialogSubtitleBlur} // Update global state on blur
                />

                <FormControl fullWidth>
                    <InputLabel>Put after which Dialog Prompt?</InputLabel>
                    <Select
                        label="Dialog Name"
                        value={insertLessonAndDialogId}
                        onChange={handleSelectChange}
                        error={!isInsertDialogPromptValid} // Set error prop to true if invalid
                    >
                        <MenuItem key={-1} value="">Select a Lesson or Dialog</MenuItem>
                        {courseList.flatMap((course) =>
                            lessonDictionary[course.id]
                                ? lessonDictionary[course.id].flatMap((lesson) => [
                                    <MenuItem key={-lesson.id} value={`${lesson.id}-`} sx={{ fontWeight: lesson.is_hidden ? 200 : 400, opacity: lesson.is_hidden ? 0.4 : 1 }}>
                                        {lesson.is_hidden && "[Hidden]"} {course.name} :: {lesson.name} :: First
                                    </MenuItem>,
                                    dialogDictionary[lesson.id]
                                        ? dialogDictionary[lesson.id].map((dialog) => (
                                            <MenuItem key={dialog.id} value={`${lesson.id}-${dialog.id}`} sx={{ fontWeight: dialog.is_hidden ? 200 : 400, opacity: dialog.is_hidden ? 0.4 : 1 }}>
                                                {dialog.is_hidden && "[Hidden]"} {course.name} :: {lesson.name} :: {dialog.title}
                                            </MenuItem>
                                        ))
                                        : []
                                ])
                                : []
                        )}
                    </Select>
                </FormControl>
                <Button variant="contained" onClick={handleInsertDialogPrompt} disabled={isInserting || isGenerating}>
                    Insert New
                    {(isInserting || isGenerating) && <CircularProgress size={16} sx={{ ml: 2 }} />}
                </Button>

                <Divider sx={{ borderColor: '#FFBE19', borderWidth: '2px', mb: 2 }} />  {/* Customized Divider */}
                <Typography variant="h4">
                    Add to old Dialog
                </Typography>

                <FormControl fullWidth>
                    <InputLabel>Add to which Dialog?</InputLabel>
                    <Select
                        label="Dialog Name"
                        value={insertLessonAndDialogId}
                        onChange={handleSelectChange}
                        error={!isInsertDialogPromptValid} // Set error prop to true if invalid
                    >
                        <MenuItem key={-1} value="">Select a Lesson or Dialog</MenuItem>
                        {courseList.flatMap((course) =>
                            lessonDictionary[course.id]
                                ? lessonDictionary[course.id].flatMap((lesson) => [
                                    dialogDictionary[lesson.id]
                                        ? dialogDictionary[lesson.id].map((dialog) => (
                                            <MenuItem key={dialog.id} value={`${lesson.id}-${dialog.id}`} sx={{ fontWeight: dialog.is_hidden ? 200 : 400, opacity: dialog.is_hidden ? 0.4 : 1 }}>
                                                {dialog.is_hidden && "[Hidden]"} {course.name} :: {lesson.name} :: {dialog.title}
                                            </MenuItem>
                                        ))
                                        : []
                                ])
                                : []
                        )}
                    </Select>
                </FormControl>
                <Button variant="contained" onClick={handleUpdateDialogPrompt} disabled={isInserting || isGenerating}>
                    Add to Old
                    {(isInserting || isGenerating) && <CircularProgress size={16} sx={{ ml: 2 }} />}
                </Button>

            </>)}
        </Stack >
        {/* Error dialog */}
        {dialog}
    </>);
};

export default DialogPlanForm;
